import {Component, EventEmitter, Input, Output} from '@angular/core';
import {CalendarEntry} from "../../../model/calendar";
import {catchError, finalize, map, of, switchMap, tap} from "rxjs";
import {StudentColLessonScheduleEvents} from "../../../model/v3/booking";
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";
import {Utils} from "../../../utils/lesson-utils";

@Component({
    selector: 'app-student-lessons-calendar',
    templateUrl: './student-lessons-calendar.component.html',
    styleUrls: ['./student-lessons-calendar.component.scss'],
    standalone: false
})
export class StudentLessonsCalendarComponent {
  private static readonly WEEK_MS = 7 * 24 * 60 * 60 * 1000;

  loading = true;
  events: CalendarEntry[] = [];
  schedules: StudentColLessonScheduleEvents[] = [];
  private _focusDate: Date;
  private _studentId: number;

  @Input()
  set studentId(studentId: number) {
    this._studentId = studentId;

    this.loadCalendarEvents();
  }

  @Output()
  openHelpModal = new EventEmitter<void>();

  set focusDate(date: Date) {
    this._focusDate = date;

    this.loadCalendarEvents();
  }

  constructor(
    private nativeApiProvider: NativeServiceApiProvider,
    private authService: AuthorizationServiceProvider
  ) {
  }

  loadCalendarEvents(): void {
    if (!this._studentId || !this._focusDate) {
      return;
    }
    this.loading = true;

    this.getStudentWeekSchedules().pipe(
      tap((schedules) => (this.schedules = schedules)),
      map((schedules) => this.mapSchedulesToCalendarEntries(schedules)),
      tap((events) => (this.events = events)),
      catchError(() => {
        this.schedules = [];
        this.events = [];
        return of([]);
      }),
      finalize(() => (this.loading = false))
    ).subscribe();
  }

  getStudentWeekSchedules() {
    const weekStart = Utils.getWeekStartDate(this._focusDate);
    const weekEnd = new Date(weekStart.getTime() + StudentLessonsCalendarComponent.WEEK_MS);
    return this.authService.getAuthDetailsService().pipe(
      switchMap((auth) => auth.getSelfSchoolId()),
      switchMap((schoolId) => this.nativeApiProvider.colV3().pipe(
        switchMap((api) =>
          api.getStudentLessonSchedules(
            schoolId,
            this._studentId,
            weekStart,
            weekEnd,
            null
          )
        )
      ))
    );
  }

  private mapSchedulesToCalendarEntries(
    schedules: StudentColLessonScheduleEvents[]
  ): CalendarEntry[] {
    return (schedules ?? [])
      .map((schedule) => this.mapScheduleToCalendarEntry(schedule))
      .filter((entry): entry is CalendarEntry => !!entry);
  }

  private mapScheduleToCalendarEntry(schedule: StudentColLessonScheduleEvents): CalendarEntry | null {
    const firstEvent = schedule.events?.[0];
    if (!firstEvent?.eventDate) {
      return null;
    }
    const dateTo = new Date();
    const dateFrom = firstEvent.eventDate;
    const duration = firstEvent.duration ?? 0;
    dateTo.setTime(dateFrom.getTime() + duration);
    const scheduleName = schedule.schedule?.name ?? '';
    const title = scheduleName.split('[')[0].trim();
    return new CalendarEntry(schedule.schedule.id, 1, dateFrom, dateTo, () => title, null, schedule, `schedule`);
  }
}
